import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { usePreferencesMutation } from '../api/preferences/updatePreferences';
import { PREFERENCE_MESSAGES } from '../pages/Preferences/constants';
import { useSnackbarsContext } from '../store/SnackbarProvider';
import { useWelcomeQuery } from '../api/getWelcomeDetails';
import { useBillsQuery } from '../api/getBills';
import { usePoliciesQuery } from '../api/getPolicies';
import { usePreferencesQuery } from '../api/preferences/getPreferences';
import {
  ExistingPreference,
  Notification,
  PolicyPreferences,
  PreferenceType,
} from '@aaa-ncnu-ie/digital-api-customer-prefs';

type PreferencesMutationType = 'paperless' | 'text' | 'update';
interface UpdatePreferencesType {
  type: PreferencesMutationType;
  enrolled: boolean;
  isBillingAndTextIneligiblePolicies?: string[];
  billingAndPaymentEnabled?: boolean;
  policyDocumentsEnabled?: boolean;
  email?: string;
  phone?: string;
  policyNumber?: string;
  onSuccess?: (response: any) => void;
  onError?: (error: unknown) => void;
}

interface UseUpdatePreferencesHook {
  updatePreferences: (args: UpdatePreferencesType) => void;
  isLoading: boolean;
}

export const isEnrolled = (
  selection = {} as UserSelection,
  type: 'email' | 'text' = 'email'
) => {
  if (type === 'email') {
    return [
      'policyDocuments',
      'billingAndPayment',
      'paymentReminders',
      'paymentConfirmation',
    ].some((item) => selection[item] === 'E');
  } else {
    return selection.textAlert === 'E';
  }
};

export const isPending = (selection = {} as UserSelection) =>
  [
    'policyDocuments',
    'billingAndPayment',
    'paymentReminders',
    'paymentConfirmation',
  ].some((item) => selection[item] === 'P');

const isEnrollOrPending = (selection: UserSelection, item: string) =>
  ['E', 'P'].includes(selection[item]);

export const isEnrolledOrPendingBillingAnPayment = (
  selection = {} as UserSelection
) =>
  ['billingAndPayment', 'paymentReminders', 'paymentConfirmation'].some(
    (item) => isEnrollOrPending(selection, item)
  );

export const areAnyEnrolledOrPending = (selection = {} as UserSelection) =>
  isEnrollOrPending(selection, 'policyDocuments') ||
  isEnrolledOrPendingBillingAnPayment(selection);

const updatePreferenceAction = (
  preferenceType: PreferenceType,
  enrolled: boolean
) => {
  preferenceType.preferenceAction = enrolled ? 'OPT_IN' : 'OPT_OUT';
};

const updateEmailNotificationPreferences = (
  policyPreference: PolicyPreferences,
  enrolled: boolean
) => {
  const emailNotification = policyPreference.notification.find(
    (notification: Notification) => notification.channelType === 'EMAIL'
  );
  emailNotification?.preferenceTypes.forEach((preferenceType: PreferenceType) =>
    updatePreferenceAction(preferenceType, enrolled)
  );
};

const updateExistingPreference = (
  existingPreference: ExistingPreference,
  enrolled: boolean
) => {
  existingPreference.policyPreferences.forEach(
    (policyPreference: PolicyPreferences) =>
      updateEmailNotificationPreferences(policyPreference, enrolled)
  );
};

const useUpdatePreferences = (): UseUpdatePreferencesHook => {
  const queryClient = useQueryClient();
  const updatePreferencesMutation = usePreferencesMutation();
  const { enqueueSnackbar } = useSnackbarsContext();
  const { data: welcomeData } = useWelcomeQuery();
  const { data: billsData } = useBillsQuery();
  const authToken = welcomeData?.authToken ?? '';
  const { data: policiesData } = usePoliciesQuery();

  // Only use policies that are available in both the /bills (CDH) and /policies (GPD) responses
  const verifiedPolicies = policiesData?.policies.filter((policy) =>
    billsData?.payments.find(
      (payment) => payment.policyNumber === policy.policyNumber
    )
  );
  const { data: preferencesData } = usePreferencesQuery(
    authToken,
    verifiedPolicies?.map((policy) => ({
      policyNumber: policy.policyNumber,
      policySourceSystem: policy.dataSource,
    }))
  );

  const updatePreferences = useCallback(
    ({
      type,
      enrolled,
      isBillingAndTextIneligiblePolicies,
      billingAndPaymentEnabled,
      policyDocumentsEnabled,
      email,
      phone,
      policyNumber,
      onSuccess,
      onError,
    }: UpdatePreferencesType) => {
      let updatedSelections = preferencesData?.selections;
      let updatedPreferences = preferencesData?.existingPreferences;
      if (updatedSelections && updatedPreferences) {
        if (policyNumber) {
          updatedSelections = updatedSelections.filter(
            (selection) => selection.policyNumber === policyNumber
          );
          updatedPreferences = updatedPreferences.filter(
            (preference) => preference.policyNumber === policyNumber
          );
        }
        if (type === 'text') {
          updatedSelections?.map((selection) => {
            selection.phone = phone ?? '';
            selection.email = email ?? '';
            selection.textAlert = enrolled ?? false;
            return selection;
          });
        } else {
          updatedPreferences?.forEach((existingPreference) => {
            const shouldUpdatePreference =
              !policyNumber || existingPreference.policyNumber === policyNumber;
            if (shouldUpdatePreference) {
              updateExistingPreference(existingPreference, enrolled);
            }
          });

          updatedSelections?.map((selection) => {
            const isBillingEnrolled = () => {
              if (
                isBillingAndTextIneligiblePolicies?.includes(
                  selection.policyNumber
                )
              )
                return false;

              return (
                billingAndPaymentEnabled ??
                enrolled ??
                isEnrolledOrPendingBillingAnPayment(selection)
              );
            };

            const isPolicyEnrolled = isEnrollOrPending(
              selection,
              'policyDocuments'
            );

            selection.policyDocuments =
              policyDocumentsEnabled ?? enrolled ?? isPolicyEnrolled;
            selection.billingAndPayment = isBillingEnrolled();
            selection.paymentReminders = isBillingEnrolled();
            selection.paymentConfirmation = isBillingEnrolled();

            return selection;
          });
        }
        const policies: { [key: string]: string } = {};
        preferencesData?.selections.forEach((selection) => {
          const foundPolicy = policiesData?.policies.find(
            (policy) => policy.policyNumber === selection.policyNumber
          );
          policies[selection.policyNumber as string] =
            foundPolicy?.dataSource ?? '';
        });

        const preferencesRequest = {
          authToken: welcomeData?.authToken ?? '',
          existingPreferences: updatedPreferences,
          selections: updatedSelections,
          policies: JSON.stringify(policies),
          customerid: welcomeData?.userInfo?.custKey ?? '',
        };

        updatePreferencesMutation.mutateAsync(preferencesRequest, {
          onSuccess: async (response) => {
            if (onSuccess) onSuccess(response);
            await queryClient.invalidateQueries({
              queryKey: ['preferences'],
            });
            enqueueSnackbar({
              variant: 'success',
              title:
                PREFERENCE_MESSAGES.success[type][
                  enrolled ? 'enrolled' : 'unenrolled'
                ],
            });
            window.scrollTo(0, 0);
          },
          onError: (error) => {
            if (onError) onError(error);
            enqueueSnackbar({
              variant: 'error',
              key: 'preferences-update-error',
              title: PREFERENCE_MESSAGES.error.title,
              content: PREFERENCE_MESSAGES.error.message,
            });
            window.scrollTo(0, 0);
          },
        });
      }
    },
    [welcomeData, policiesData, billsData, preferencesData]
  );

  return {
    updatePreferences,
    isLoading: updatePreferencesMutation.isPending,
  };
};

export default useUpdatePreferences;
